import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatUSD, formatInt } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, LogOut, Package, Mail, Star, StarOff } from "lucide-react";
import { EquipmentForm } from "@/components/EquipmentForm";

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const [tab, setTab] = useState("inventory");
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | equipment obj

  const refresh = () => {
    api.get("/equipment", { params: { include_sold: true, limit: 200 } }).then((r) => setItems(r.data));
    api.get("/leads").then((r) => setLeads(r.data));
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  if (loading) return <div className="p-10 text-center text-white/60">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this listing permanently?")) return;
    try {
      await api.delete(`/equipment/${id}`);
      toast.success("Listing deleted");
      refresh();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await api.put(`/equipment/${item.id}`, { featured: !item.featured });
      refresh();
    } catch {
      toast.error("Failed to update");
    }
  };

  const updateLead = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      refresh();
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-[1600px] px-6 py-10 lg:px-10">
      {/* HEADER */}
      <div className="flex flex-col gap-4 border-b border-white/10 pb-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FFB800]">Control Room</div>
          <h1 className="mt-2 font-display text-4xl font-black uppercase">Dealer Dashboard</h1>
          <p className="mt-1 font-mono text-xs uppercase tracking-wider text-white/50">{user.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="border border-[#27272A] px-4 py-2 font-display text-xs font-bold uppercase tracking-widest hover:border-[#FFB800] hover:text-[#FFB800]">View Site</Link>
          <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-2 border border-[#27272A] px-4 py-2 font-display text-xs font-bold uppercase tracking-widest hover:border-red-500 hover:text-red-400">
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </div>

      {/* TABS */}
      <div className="mt-6 flex gap-px border border-[#27272A] bg-[#27272A]">
        <TabBtn active={tab === "inventory"} onClick={() => setTab("inventory")} icon={Package} label={`Inventory (${items.length})`} testid="tab-inventory" />
        <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} icon={Mail} label={`Leads (${leads.filter(l => l.status === "new").length} new)`} testid="tab-leads" />
      </div>

      {/* INVENTORY */}
      {tab === "inventory" && (
        <div className="mt-8">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold uppercase">Equipment Listings</h2>
            <button
              onClick={() => setEditing("new")}
              data-testid="add-equipment-btn"
              className="flex items-center gap-2 bg-[#FFB800] px-6 py-3 font-display text-xs font-bold uppercase tracking-widest text-black hover:bg-[#E5A600]"
            >
              <Plus className="h-4 w-4" /> New Listing
            </button>
          </div>

          <div className="overflow-x-auto border border-[#27272A]">
            <table className="w-full">
              <thead className="bg-[#141415] font-mono text-[10px] uppercase tracking-widest text-white/50">
                <tr>
                  <th className="p-4 text-left">Machine</th>
                  <th className="p-4 text-left">Category</th>
                  <th className="p-4 text-right">Year</th>
                  <th className="p-4 text-right">Hours</th>
                  <th className="p-4 text-right">Price</th>
                  <th className="p-4 text-left">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#27272A]">
                {items.map((it) => (
                  <tr key={it.id} className="hover:bg-[#141415]" data-testid={`admin-row-${it.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img src={it.images?.[0]} alt="" className="h-12 w-16 border border-[#27272A] object-cover" />
                        <div>
                          <div className="font-display font-bold uppercase">{it.title}</div>
                          <div className="font-mono text-[10px] uppercase tracking-wider text-white/50">{it.brand} · {it.model}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 font-mono text-xs">{it.category}</td>
                    <td className="p-4 text-right font-mono text-xs">{it.year}</td>
                    <td className="p-4 text-right font-mono text-xs">{formatInt(it.hours)}</td>
                    <td className="p-4 text-right font-display font-bold text-[#FFB800]">{formatUSD(it.price)}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {it.sold && <span className="bg-red-600/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-400">Sold</span>}
                        {it.featured && <span className="bg-[#FFB800]/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[#FFB800]">Featured</span>}
                        {!it.sold && !it.featured && <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">Active</span>}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => toggleFeatured(it)} title="Toggle featured" className="p-2 hover:bg-[#27272A]">
                          {it.featured ? <Star className="h-4 w-4 text-[#FFB800]" fill="currentColor" /> : <StarOff className="h-4 w-4 text-white/50" />}
                        </button>
                        <button onClick={() => setEditing(it)} data-testid={`edit-btn-${it.id}`} className="p-2 text-white/70 hover:bg-[#27272A] hover:text-[#FFB800]"><Edit2 className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(it.id)} data-testid={`delete-btn-${it.id}`} className="p-2 text-white/70 hover:bg-[#27272A] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* LEADS */}
      {tab === "leads" && (
        <div className="mt-8">
          <h2 className="mb-6 font-display text-2xl font-bold uppercase">Customer Leads</h2>
          <div className="grid gap-4">
            {leads.length === 0 && <div className="border border-dashed border-[#27272A] p-12 text-center text-white/50">No leads yet.</div>}
            {leads.map((lead) => (
              <div key={lead.id} data-testid={`lead-${lead.id}`} className="border border-[#27272A] bg-[#141415] p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                        lead.lead_type === "quote" ? "bg-[#FFB800]/20 text-[#FFB800]" : "bg-blue-500/20 text-blue-300"
                      }`}>{lead.lead_type}</span>
                      <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${
                        lead.status === "new" ? "bg-green-500/20 text-green-300" :
                        lead.status === "contacted" ? "bg-white/10 text-white/70" : "bg-white/5 text-white/40"
                      }`}>{lead.status}</span>
                    </div>
                    <div className="mt-2 font-display text-xl font-bold">{lead.name}</div>
                    <div className="font-mono text-xs text-white/60">{lead.email} · {lead.phone}</div>
                    {lead.equipment_title && <div className="mt-1 font-mono text-[11px] uppercase tracking-wider text-[#FFB800]">re: {lead.equipment_title}</div>}
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={lead.status}
                      onChange={(e) => updateLead(lead.id, e.target.value)}
                      data-testid={`lead-status-${lead.id}`}
                      className="border border-[#27272A] bg-[#0a0a0b] px-3 py-2 font-mono text-xs uppercase text-white focus:border-[#FFB800] focus:outline-none"
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
                <p className="mt-3 border-t border-[#27272A] pt-3 text-sm text-white/70">{lead.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODAL */}
      {editing && (
        <EquipmentForm
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { refresh(); setEditing(null); }}
        />
      )}
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex flex-1 items-center justify-center gap-2 px-6 py-4 font-display text-xs font-bold uppercase tracking-widest transition-colors ${
      active ? "bg-[#FFB800] text-black" : "bg-[#0a0a0b] text-white/70 hover:text-[#FFB800]"
    }`}
  >
    <Icon className="h-4 w-4" /> {label}
  </button>
);
